import { z } from 'zod';

// ── Request schemas ────────────────────────────────────────────────────────────

// Legacy direct-signup schema (kept for backward compat; new flow uses initiate/complete)
export const SignupRequestSchema = z.object({
  email: z.string().email('Invalid email address').toLowerCase().trim(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters'),
  displayName: z.string().min(1, 'Display name is required').max(100).trim().optional(),
});

// ── Email-based signup (2-step) ────────────────────────────────────────────────

export const InitiateSignupRequestSchema = z.object({
  email: z.string().email('Invalid email address').toLowerCase().trim(),
});

export const CompleteSignupRequestSchema = z.object({
  token: z.string().min(1, 'Token is required'),
  displayName: z.string().min(1, 'Full name is required').max(100).trim(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters'),
});

export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email address').toLowerCase().trim(),
  password: z.string().min(1, 'Password is required'),
});

// ── View preferences ───────────────────────────────────────────────────────────

export const ViewPreferencesSchema = z.object({
  collapseTitles: z.boolean().default(false),
  collapseProjects: z.boolean().default(false),
});

export const UpdateViewPreferencesSchema = z.object({
  viewPreferences: ViewPreferencesSchema,
});

export type ViewPreferences = z.infer<typeof ViewPreferencesSchema>;
export type UpdateViewPreferences = z.infer<typeof UpdateViewPreferencesSchema>;

// ── Response schemas ───────────────────────────────────────────────────────────

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  emailVerifiedAt: z.string().datetime().nullable(),
  yearsOfExperience: z.number().int().nullable(),
  isAdmin: z.boolean().default(false),
  viewPreferences: ViewPreferencesSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const AuthResponseSchema = z.object({
  user: UserSchema,
});

export const ErrorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
  statusCode: z.number().int(),
});

// ── Types ──────────────────────────────────────────────────────────────────────

export type SignupRequest = z.infer<typeof SignupRequestSchema>;
export type InitiateSignupRequest = z.infer<typeof InitiateSignupRequestSchema>;
export type CompleteSignupRequest = z.infer<typeof CompleteSignupRequestSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type User = z.infer<typeof UserSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
